import { useState, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import welcome from "../../assets/welcome-hand.png";
import fire from "../../assets/fire.svg";

import { useAppContext } from "../../AppContext";
import Card from "react-bootstrap/Card";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Button from "react-bootstrap/Button";

import {
  useCategories,
  useInfiniteProducts,
  useProducts,
  useTrendingProducts,
} from "../../hooks/useProducts";

import { getProductId, getProductWithCartQuantity } from "../../utils";
import { Loader } from "../../components/commons/Loader";
import ProductCard from "../../components/commons/ProductCard";
import SeoHelmet from "../../components/commons/SeoHelmet";
import DetailsFooter from "../../components/commons/DetailsFooter";
import {
  useWishlistManager,
  useCartManager,
  usePageTracking,
  useProductNavigation,
  useMarginBottomStyle,
} from "../../hooks/useProductActions";
import { getParsedLocalStorageItem } from "../../components/Navbar/Navbar";
import { localStorageGetItem } from "../../App";
import PaginationControl from "../../components/commons/PaginationControl";
import HomeBanners from "../../components/HomeBanners";
import QuickLinkCards from "../../components/QuickLinkCards";
import DeliveryCountdownBar from "../../components/commons/DeliveryCountdownBar";

const Home = () => {
  const { showToast, cartItems, isMobile } = useAppContext();

  const [activeCategories, setActiveCategories] = useState({
    categoryName: "All",
  });
  const [viewAll, setViewAll] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [paginationState, setPaginationState] = useState({
    currentPage: 1,
    size: 20,
  });

  const navigate = useNavigate();
  const scrollContainerRef = useRef(null);

  const userData = getParsedLocalStorageItem("userData");
  const userToken = localStorageGetItem("userToken");
  const isLoggedIn = !!userData && !!userToken;

  // React Query hooks

  const { data: trendingData = [], isLoading: loadingTrending } =
    useTrendingProducts({ showToast, navigate });
  const { data: categoryData, isLoading: loadingCategories } = useCategories({
    showToast,
    navigate,
  });

  const { data: desktopProductsData, isLoading: loadingDesktopProducts } =
    useProducts(paginationState.currentPage, paginationState.size, "", {
      showToast,
      navigate,
      enabled: !isMobile,
    });
  const isAll = activeCategories.categoryName?.toLowerCase() === "all";
  const isTrending =
    activeCategories.categoryName?.toLowerCase() === "trending";

  const categoryId =
    !isAll && !isTrending
      ? activeCategories.sku || activeCategories.id
      : undefined;

  const {
    data: infiniteData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    status: infiniteStatus,
    isFetching: isFetchingInfinite,
  } = useInfiniteProducts(
    paginationState.size,
    isAll ? undefined : categoryId,
    "",
    {
      showToast,
      navigate,
      enabled: isMobile && !isTrending,
    },
  );

  const categories = useMemo(
    () => (Array.isArray(categoryData) ? categoryData : []),
    [categoryData],
  );

  const trendingProducts = useMemo(
    () => (Array.isArray(trendingData) ? trendingData : []),
    [trendingData],
  );

  const pagination = desktopProductsData?.pagination || {
    currentPage: paginationState.currentPage,
    totalPages: 1,
    totalProducts: desktopProductsData?.totalProducts || 0,
  };

  const getBaseProducts = () => {
    if (isMobile) {
      return infiniteData?.pages?.flatMap((p) => p.products ?? []) ?? [];
    }
    return desktopProductsData?.products ?? [];
  };
  const products = getBaseProducts();

  const allCategories = useMemo(() => {
    const mapped = categories.map((c) => ({
      id: c.id ?? c.sku ?? String(c.categoryName),
      categoryName: c.categoryName,
      image: c.categoryImage,
      sku: c.sku,
    }));

    return [
      { id: "all", categoryName: "All", isSystem: true },
      { id: "trending", categoryName: "Trending", isSystem: true },
      ...mapped,
    ];
  }, [categories]);

  const isCategoryLoading = useMemo(() => {
    const name = (
      activeCategories.categoryName ||
      activeCategories.name ||
      "All"
    ).toLowerCase();

    if (name === "trending") return loadingTrending;
    if (isMobile) {
      return (
        infiniteStatus === "loading" ||
        infiniteStatus === "pending" ||
        (isFetchingInfinite &&
          !isFetchingNextPage &&
          (!infiniteData || infiniteData.pages.length === 0)) ||
        infiniteData === undefined
      );
    }
    return false;
  }, [
    activeCategories,
    infiniteStatus,
    isFetchingInfinite,
    isFetchingNextPage,
    infiniteData,
    loadingTrending,
    isMobile,
  ]);

  const getFilteredCategories = () => {
    const name = activeCategories?.categoryName?.toLowerCase();

    if (name === "trending") return trendingProducts;
    if (isMobile)
      return infiniteData?.pages?.flatMap((p) => p.products ?? []) ?? [];

    return [];
  };

  const filteredCategories = getFilteredCategories();

  const displayCategories = viewAll ? categories : categories?.slice(0, 6);

  // Consolidate current visible products for managers
  const visibleProducts = useMemo(() => {
    return [...products, ...trendingProducts, ...filteredCategories];
  }, [products, trendingProducts, filteredCategories]);

  usePageTracking("Home page");

  const { toggleFill, isInWishlist } = useWishlistManager(visibleProducts);

  const { handleIncreaseQuantity, handleDecreaseQuantity } = useCartManager(
    visibleProducts,
    [],
    "Home page",
  );

  const { handleShowProductModal, handleCategoryChange } =
    useProductNavigation();

  const marginBottomStyle = useMarginBottomStyle(filteredCategories.length);

  const isLoading = loadingCategories || (!isMobile && loadingDesktopProducts);

  const handleImageClick = () => {
    setIsPaused(!isPaused);
  };

  const handleScroll = (direction) => {
    const container = scrollContainerRef.current;
    const scrollAmount =
      direction === "left"
        ? -container.clientWidth * 0.8
        : container.clientWidth * 0.8;
    container.scrollBy({ left: scrollAmount, behavior: "smooth" });
  };

  const filterProducts = (category) => {
    if (isMobile) {
      setActiveCategories(category);
    } else {
      handleCategoryChange(category);
    }
  };

  const handlePageChange = (page) => {
    setPaginationState((prevState) => ({
      ...prevState,
      currentPage: page,
    }));
  };

  const websiteStructuredData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Virtue's Shop",
    url: "https://app.virtuesshop.co/",
    potentialAction: [
      {
        "@type": "SearchAction",
        target: "https://app.virtuesshop.co/search",
      },
    ],
  };

  const organizationStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Virtue's Shop",
    url: "https://app.virtuesshop.co/",
    contactPoint: {
      "@type": "ContactPoint",
      telephone: "+234 9167682871",
      contactType: "Customer Service",
      areaServed: "NG",
      availableLanguage: "en",
    },
  };

  return (
    <>
      <SeoHelmet
        title="Virtue's Shop - Fresh grocery delivery in Lagos"
        description="Premium fresh grocery delivery service in Lagos and Ogun State. 600+ products, Grade A quality, 100% delivery rate."
        keywords="Food, Fresh food delivery, Foodstuff shop in Lagos, Affordable food store"
        image="https://app.virtuesshop.co/virtue-cover.png"
        url="https://app.virtuesshop.co/"
        schema={[websiteStructuredData, organizationStructuredData]}
      />

      <DeliveryCountdownBar
        isMobile={isMobile}
        style={{ marginTop: isMobile ? "49px" : 0 }}
      />

      <div className="px-3 px-md-0 px-xxl-5 mx-lg-5 product-page pt-3 pb-6 pt-md-0 pb-md-0">
        <div className="d-flex justify-content-between align-items-center mb-0 mb-md-2 mt-0 mt-md-4 px-xl-2">
          <div className="d-flex gap-1 align-items-center ">
            <h6
              style={{
                fontWeight: "600",
                color: "#344054",
                letterSpacing: "-2%",
                fontSize: isMobile ? "14px" : "18px",
              }}
              className="my-auto"
            >
              Welcome {isLoggedIn ? userData?.firstName : ""}
              {!isLoggedIn && "👋"}
            </h6>
            {isLoggedIn && (
              <img
                src={welcome}
                alt="welcome to virtuesshop"
                style={{ width: "21px", height: "23px" }}
              />
            )}
          </div>
        </div>

        <HomeBanners
          isMobile={isMobile}
          isPaused={isPaused}
          handleImageClick={handleImageClick}
        />
        <QuickLinkCards isMobile={isMobile} navigate={navigate} />

        {isLoading ? (
          <Loader />
        ) : (
          <>
            {!isMobile && (
              <>
                <section className="px-0 px-xl-2 py-3 position-relative">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h4
                      className="mb-0"
                      style={{
                        color: "#23272f",
                        fontWeight: "700",
                        fontSize: "18px",
                        lineHeight: 1.3,
                      }}
                    >
                      Trending Products <img src={fire} alt="fire" />
                    </h4>
                    {trendingProducts.length > 0 && (
                      <div className="d-flex gap-2">
                        <button
                          type="button"
                          className="rail-nav-btn"
                          aria-label="Scroll trending left"
                          onClick={() => handleScroll("left")}
                        >
                          &#8249;
                        </button>
                        <button
                          type="button"
                          className="rail-nav-btn"
                          aria-label="Scroll trending right"
                          onClick={() => handleScroll("right")}
                        >
                          &#8250;
                        </button>
                      </div>
                    )}
                  </div>

                  {loadingTrending && pagination.currentPage === 1 ? (
                    <Loader />
                  ) : trendingProducts.length > 0 ? (
                    <div>
                      <div
                        className="scroll-container"
                        style={{
                          scrollSnapType: "x mandatory",
                          WebkitOverflowScrolling: "touch",
                        }}
                        ref={scrollContainerRef}
                      >
                        {trendingProducts.map((product) => {
                          const displayProduct = getProductWithCartQuantity(
                            product,
                            cartItems,
                          );

                          return (
                            <ProductCard
                              key={product?.id}
                              product={displayProduct}
                              isInWishlist={isInWishlist(getProductId(product))}
                              onShowModal={handleShowProductModal}
                              onToggleFill={toggleFill}
                              onIncreaseQuantity={handleIncreaseQuantity}
                              onDecreaseQuantity={handleDecreaseQuantity}
                              isHorizontal={true}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <div
                      className="text-center fw-medium"
                      style={{ padding: "100px" }}
                    >
                      No trending products available
                    </div>
                  )}
                </section>

                <section className="px-0 px-xl-2 pb-1">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <h4
                      style={{
                        color: "#23272f",
                        fontWeight: "700",
                        fontSize: "18px",
                        lineHeight: 1.3,
                      }}
                    >
                      Top Categories
                    </h4>
                    {categories?.length > 6 && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => setViewAll(!viewAll)}
                        style={{
                          color: "#344054",
                          borderColor: "#EAECF0",
                          fontWeight: "500",
                        }}
                      >
                        {viewAll ? "Show Less" : "View All"}
                      </Button>
                    )}
                  </div>

                  {loadingCategories ? (
                    <Loader />
                  ) : displayCategories?.length > 0 ? (
                    <Row className="category-row">
                      {displayCategories?.map((category) => (
                        <Col xs={4} md={2} key={category?.id} className="mb-3">
                          <Card className="border-0 text-align-center">
                            <div
                              style={{ height: "130px" }}
                              className="d-flex justify-content-center align-items-center text-center mx-1 mb-2"
                            >
                              <Card.Img
                                src={category?.categoryImage}
                                alt={category?.categoryName}
                                style={{
                                  height: "100%",
                                  objectFit: "cover",
                                  width: "100%",
                                  cursor: "pointer",
                                }}
                                onClick={() => filterProducts(category)}
                              />
                            </div>
                            <p
                              className="text-decoration-underline text-center mt-1 mt-md-2"
                              onClick={() => filterProducts(category)}
                              style={{
                                color: "#23272f",
                                fontWeight: "700",
                                fontSize: "15px",
                                lineHeight: 1.3,
                              }}
                            >
                              {category?.categoryName}
                            </p>
                          </Card>
                        </Col>
                      ))}
                    </Row>
                  ) : (
                    <div
                      className="text-center fw-medium"
                      style={{ padding: "100px" }}
                    >
                      No categories available
                    </div>
                  )}
                </section>
              </>
            )}

            {isMobile ? (
              <section className="px-1 pb-6 mb-6">
                <div
                  className="category-container mb-2"
                  style={{ borderBottom: "1px solid #EAECF0" }}
                >
                  {allCategories.map((category) => (
                    <div
                      key={category.id}
                      className={`category-item ${
                        activeCategories.categoryName === category.categoryName
                          ? "active"
                          : ""
                      }`}
                      onClick={() => filterProducts(category)}
                    >
                      {category.categoryName}
                    </div>
                  ))}
                </div>
                {isCategoryLoading ? (
                  <Loader />
                ) : filteredCategories?.length > 0 ? (
                  <div>
                    <InfiniteScroll
                      key={activeCategories.categoryName}
                      dataLength={filteredCategories.length}
                      next={!isTrending ? fetchNextPage : undefined}
                      hasMore={!isTrending && !!hasNextPage}
                      loader={!isTrending && isFetchingNextPage && <Loader />}
                    >
                      <Row style={marginBottomStyle}>
                        {filteredCategories.map((product) => {
                          const displayProduct = getProductWithCartQuantity(
                            product,
                            cartItems,
                          );

                          return (
                            <Col
                              xs={6}
                              md={3}
                              className="mb-3 mb-md-4"
                              key={getProductId(product)}
                            >
                              <ProductCard
                                product={displayProduct}
                                isInWishlist={isInWishlist(
                                  getProductId(product),
                                )}
                                onShowModal={handleShowProductModal}
                                onToggleFill={toggleFill}
                                onIncreaseQuantity={handleIncreaseQuantity}
                                onDecreaseQuantity={handleDecreaseQuantity}
                                isMobile={true}
                              />
                            </Col>
                          );
                        })}
                      </Row>
                    </InfiniteScroll>
                  </div>
                ) : (
                  <div
                    className="text-center fw-medium"
                    style={{ padding: "150px 100px" }}
                  >
                    No products available under this category
                  </div>
                )}
              </section>
            ) : (
              <>
                <section className="px-0 px-xl-2 pb-3">
                  <h4
                    className="mb-2"
                    style={{
                      color: "#23272f",
                      fontWeight: "700",
                      fontSize: "18px",
                      lineHeight: 1.3,
                    }}
                  >
                    Products{" "}
                    <span style={{ color: "#98A2B3" }}>
                      ({pagination?.totalProducts})
                    </span>
                  </h4>
                  {loadingDesktopProducts ? (
                    <Loader />
                  ) : products.length > 0 ? (
                    <Row xs={2} md={3} lg={4} xl={5}>
                      {products.map((product) => {
                        const displayProduct = getProductWithCartQuantity(
                          product,
                          cartItems,
                        );
                        return (
                          <Col
                            className="mb-3 mb-md-4"
                            key={getProductId(product)}
                          >
                            <ProductCard
                              product={displayProduct}
                              isInWishlist={isInWishlist(getProductId(product))}
                              onShowModal={handleShowProductModal}
                              onToggleFill={toggleFill}
                              onIncreaseQuantity={handleIncreaseQuantity}
                              onDecreaseQuantity={handleDecreaseQuantity}
                            />
                          </Col>
                        );
                      })}
                    </Row>
                  ) : (
                    <div
                      className="text-center fw-medium"
                      style={{ padding: "100px" }}
                    >
                      No products available
                    </div>
                  )}

                  {products.length > 0 && (
                    <div
                      className="d-flex justify-content-between align-items-center my-4"
                      style={{ height: "50px" }}
                    >
                      <p style={{ fontWeight: "500", color: "#919EAB" }}>
                        {products?.length} results
                      </p>
                      {pagination.totalPages > 1 && (
                        <PaginationControl
                          currentPage={pagination.currentPage}
                          totalPages={pagination.totalPages}
                          onPageChange={handlePageChange}
                        />
                      )}
                    </div>
                  )}
                </section>
                <DetailsFooter />
              </>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default Home;
